'use client';

import { CaretDown } from '@phosphor-icons/react/ssr';
import type { ComponentProps, ElementType, ReactNode } from 'react';

import {
  Select as SelectRoot,
  SelectContent,
  SelectGroup,
  SelectItem as SelectItemPrimitive,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useFieldControl, useFieldLabelId } from './field';
import { selectTrigger } from './select-styles';

export type SelectProps = Omit<ComponentProps<typeof SelectRoot>, 'children'> & {
  /** Shown until a value is chosen. */
  placeholder?: ReactNode;
  /** Figma "Size": Small 32 · Medium 40 (default) · Large 48. */
  size?: 'sm' | 'md' | 'lg';
  leadingIcon?: ElementType<{ className?: string }>;
  id?: string;
  /** Class for the trigger (width, margins). */
  className?: string;
  'aria-label'?: string;
  /** Error state without a Field (inside a Field, `error` sets it). */
  'aria-invalid'?: boolean;
  /** SelectItem, SelectGroup, SelectHeader, SelectDivider. */
  children: ReactNode;
};

/**
 * Choose one option from a list (Figma: Components ↳ Dropdown ↳ Select).
 * Radix Select underneath: keyboard, typeahead and screen-reader support.
 * Inside a Field it picks up id, description, invalid and disabled. For
 * choosing several, pair `SelectButton` with a `Menu` of checkbox items.
 */
export function Select({
  placeholder,
  size,
  leadingIcon,
  id,
  className,
  children,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  ...props
}: SelectProps) {
  const { disabled, ...control } = useFieldControl({
    id,
    disabled: props.disabled,
    'aria-invalid': ariaInvalid || undefined,
  });
  // The listbox is named like the trigger: by the Field label, or aria-label.
  const labelId = useFieldLabelId();
  return (
    <SelectRoot {...props} disabled={disabled}>
      <SelectTrigger
        size={size}
        leadingIcon={leadingIcon}
        className={className}
        aria-label={ariaLabel}
        {...control}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent
        aria-labelledby={labelId}
        aria-label={labelId ? undefined : ariaLabel}
      >
        {children}
      </SelectContent>
    </SelectRoot>
  );
}

/** One option. `value` is what `onValueChange` receives. */
export const SelectItem = SelectItemPrimitive;
/** Groups options under a SelectHeader. */
export { SelectGroup };
/** Figma Menu Header: an Overline title for a group. */
export const SelectHeader = SelectLabel;
/** Figma Menu Divider. */
export const SelectDivider = SelectSeparator;

export type SelectButtonProps = Omit<ComponentProps<'button'>, 'value'> & {
  size?: 'sm' | 'md' | 'lg';
  leadingIcon?: ElementType<{ className?: string }>;
  placeholder?: ReactNode;
  /** The current selection, as text. Empty shows the placeholder. */
  value?: ReactNode;
};

/**
 * A button that looks like the Select trigger, for menus a Select can't do -
 * multi-select with search and an Apply footer:
 * `<Menu><MenuTrigger asChild><SelectButton …/></MenuTrigger>…</Menu>`.
 * Shows "open" while `aria-expanded`.
 */
export function SelectButton({
  size,
  leadingIcon: LeadingIcon,
  placeholder,
  value,
  className,
  ...props
}: SelectButtonProps) {
  const styles = selectTrigger({ size });
  const control = useFieldControl(props);
  const empty = value === undefined || value === null || value === '';
  return (
    <button
      type="button"
      data-slot="select-button"
      data-placeholder={empty || undefined}
      className={styles.root({ className })}
      {...control}
    >
      {LeadingIcon && <LeadingIcon aria-hidden className={styles.leading()} />}
      <span className={styles.value()}>{empty ? placeholder : value}</span>
      <CaretDown aria-hidden className={styles.caret()} />
    </button>
  );
}
